import { createAxiosInstance } from 'api/request';
import { ActivityIndicator, Anchor, Button, Referenz, Show } from 'components/atoms';
import React, { useState } from 'react';
import { GewerbeberechtigungGeschäftsführer, GewerbeberechtigungInhaber, GewerbeberechtigungNutzer } from 'types';
import { downloadBlob } from 'utils/functions';
import { useTranslation } from 'utils/hooks';

import css from './Gewerbeberechtigung.module.scss';

interface GewerbeberechtigungProps
{
  gewerbe: GewerbeberechtigungInhaber | GewerbeberechtigungNutzer | GewerbeberechtigungGeschäftsführer;
}

const Gewerbeberechtigung: React.FC<GewerbeberechtigungProps> = (props) =>
{
  const { gewerbe } = props;
  const { t } = useTranslation(['formats', 'profiles']);
  
  const [isHistoricalFetching, setIsHistoricalFetching] = useState<boolean>(false);
  const [isCurrentFetching, setIsCurrentFetching] = useState<boolean>(false);
  
  const fetchHistorical = () =>
  {
    setIsHistoricalFetching(true);

    const url = `/gisa/${gewerbe.gisaNr}/gisa-auszug?history=true&language=de`;
    
    createAxiosInstance()
      .get(url, { responseType: 'blob', timeout: 30_000 })
      .then((response) => downloadBlob(response.data, `Gisa-Auszug-Historisch-${gewerbe.gisaNr}.pdf`))
      .finally(() => setIsHistoricalFetching(false));
  };

  const fetchCurrent = async () =>
  {
    setIsCurrentFetching(true);

    const url = `/gisa/${gewerbe.gisaNr}/gisa-auszug?history=false&language=de`;

    createAxiosInstance()
      .get(url, { responseType: 'blob', timeout: 30_000 })
      .then((response) => downloadBlob(response.data, `Gisa-Auszug-${gewerbe.gisaNr}.pdf`))
      .finally(() => setIsCurrentFetching(false));
  };

  return (
    <li className={css.item}>

      {/* Gisa Nummer */}
      <h4 className={css.gisaNummer}>
        {`${t('profiles:gewerbe.gisaNumber')}: ${gewerbe.gisaNr}`}
      </h4>

      {/* Wortlaut */}
      <p className={css.wortlaut}>{gewerbe.wortlaut}</p>

      {/* Aktiv seit */}
      <h5>{t('profiles:gewerbe.startDate')}</h5>
      {t('formats:date', { value: gewerbe.startDatum })}

      {/* Gewerberechtlicher Geschäftsführer */}
      {'geschäftsführer' in gewerbe && gewerbe.geschäftsführer && (
        <>
          <h5>{t('profiles:gewerbe.gewerberechtlicherGeschäftsführer')}</h5>
          <Referenz data={gewerbe.geschäftsführer}>
            {({ ref, href }) => <Anchor href={href}>{ref.name}</Anchor>}
          </Referenz>
        </>
      )}

      {/* Inhaber */}
      {'inhaber' in gewerbe && gewerbe.inhaber !== null && (
        <>
          <h5>{t('profiles:gewerbe.owner')}</h5>
          <Referenz data={gewerbe.inhaber}>
            {({ ref, href }) => <Anchor href={href}>{ref.name}</Anchor>}
          </Referenz>
        </>
      )}

      {/* Verwendet von */}
      {'verwendetVon' in gewerbe && gewerbe.verwendetVon.length > 0 && (
        <>
          <h5>{t('profiles:gewerbe.using')}</h5>
          <ul>
            {gewerbe.verwendetVon.map((verwender) => (
              <li key={verwender.id}>
                <Referenz data={verwender}>
                  {({ ref, href }) => <Anchor href={href}>{ref.name}</Anchor>}
                </Referenz>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Actions */}
      <div className={css.actions}>
        <Button variant="secondary" onClick={fetchCurrent}>
          {t('profiles:gewerbe.download')}
        </Button>
        <Button variant="secondary" onClick={fetchHistorical}>
          {t('profiles:gewerbe.downloadHistorical')}
        </Button>
      </div>

      {/* Loading Text */}
      <Show when={isHistoricalFetching || isCurrentFetching}>
        <div className={css.loading}>
          <ActivityIndicator />
          &emsp;
          {t('profiles:gewerbe.generating')}
        </div>
      </Show>

    </li>
  );
};

export default Gewerbeberechtigung;
